<template>
  <div class="section-editor">
    <div class="section-editor__head">
      <StField label="Section name" class="section-editor__name">
        <StTextInput
          :model-value="section.name"
          placeholder="Section name"
          @update:model-value="update('name', $event)"
        />
      </StField>
      <StField label="Reference">
        <StSelect
          :model-value="section.ref ?? 'none'"
          @update:model-value="updateRef(String($event))"
        >
          <option value="none">none</option>
          <option value="battleplans">battleplans</option>
        </StSelect>
      </StField>
      <StIconButton icon="trash" label="Delete section" variant="danger" @click="emit('delete')" />
    </div>

    <template v-if="!section.ref">
      <section class="section-editor__block">
        <h3 class="section-editor__block-title">Containers</h3>
        <RuleContainerForm
          :model-value="section.containers"
          @update:model-value="update('containers', $event)"
        />
      </section>

      <section class="section-editor__block">
        <h3 class="section-editor__block-title">Sub-sections</h3>
        <RuleSectionForm
          :model-value="section.subSections"
          @update:model-value="update('subSections', $event)"
        />
      </section>
    </template>
    <p v-else class="section-editor__ref-hint">
      This section is a <strong>{{ section.ref }}</strong> reference — its content is generated from
      the bundle when the document is previewed.
    </p>
  </div>
</template>

<script setup lang="ts">
// Edits a single RuleSection in the rules-document outline editor. Sub-sections
// (themselves sections) stay inline via RuleSectionForm — the outline lists only
// top-level sections; deeper nesting is rare and edited in place here.
import type { RuleSection, BundleRef } from '@scrolldex/shared';
import { StField, StTextInput, StSelect, StIconButton } from '../ui/index.js';
import RuleContainerForm from './RuleContainerForm.vue';
import RuleSectionForm from './RuleSectionForm.vue';

const props = defineProps<{ section: RuleSection }>();
const emit = defineEmits<{
  (e: 'update:section', value: RuleSection): void;
  (e: 'delete'): void;
}>();

function update(field: keyof RuleSection, value: unknown) {
  emit('update:section', { ...props.section, [field]: value });
}

function updateRef(refValue: string) {
  if (refValue === 'none') {
    const { ref: _omit, ...rest } = props.section;
    emit('update:section', { ...rest } as RuleSection);
  } else {
    emit('update:section', { ...props.section, ref: refValue as BundleRef });
  }
}
</script>

<style scoped>
.section-editor {
  display: flex;
  flex-direction: column;
  gap: var(--st-space-4);
}

.section-editor__head {
  display: flex;
  align-items: flex-end;
  gap: var(--st-space-2);
}
.section-editor__name {
  flex: 1;
}

.section-editor__block {
  display: flex;
  flex-direction: column;
  gap: var(--st-space-2);
}
.section-editor__block-title {
  margin: 0;
  font-size: var(--st-text-xs);
  font-weight: var(--st-weight-bold);
  text-transform: uppercase;
  letter-spacing: var(--st-tracking-wide);
  color: var(--st-text-subtle);
}

.section-editor__ref-hint {
  margin: 0;
  font-size: var(--st-text-sm);
  color: var(--st-text-muted);
  font-style: italic;
}
</style>
